import React, { useState, useEffect, useCallback, useRef } from 'react';
import { EditButton, Edit, Create, SimpleForm, TextInput, NumberInput, ReferenceInput, SelectInput, useNotify, useRefresh, Confirm } from 'react-admin';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
    CircularProgress, Typography, Box
} from '@mui/material';
import { Delete as DeleteIcon, Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase, adminBase } from './dataProvider';

const DeleteCourseQuestionButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/course_questions/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Question deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting question:', error);
            notify('Failed to delete question', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Question"
                content="Are you sure you want to delete this course question? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

const UploadTsvButton = ({ onSuccess }) => {
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [courses, setCourses] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleOpen = () => {
        setOpen(true);
        setSelectedCourse('');
        setFile(null);
        setLoadingCourses(true);
        httpClient(`${adminBase}/course_material_content/?_start=0&_end=200&_sort=title&_order=ASC`)
            .then(({ json }) => {
                setCourses(json || []);
                setLoadingCourses(false);
            })
            .catch(() => {
                notify('Failed to load courses', { type: 'error' });
                setLoadingCourses(false);
            });
    };

    const handleClose = () => {
        if (uploading) return;
        setOpen(false);
    };

    const handleUpload = async () => {
        if (!selectedCourse || !file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('course_material_id', selectedCourse);
            formData.append('file', file);

            const userId = localStorage.getItem('userId');
            const token = localStorage.getItem('token');
            const headers = { 'X-User-Id': userId };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${publicBase}/course_questions/upload_tsv`, {
                method: 'POST',
                headers,
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err?.detail || `Upload failed (${response.status})`);
            }

            const result = await response.json();
            notify(`Successfully imported ${result.imported} questions`, { type: 'success' });
            setOpen(false);
            onSuccess();
        } catch (err) {
            notify(err.message || 'Upload failed', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleOpen}
                sx={{ textTransform: 'none' }}
            >
                Upload TSV
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Import Questions from TSV</DialogTitle>
                <DialogContent dividers>
                    {/* Step 1: Select course */}
                    <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                            1. Select course material
                        </FormLabel>
                        {loadingCourses ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading courses…</Typography>
                            </Box>
                        ) : (
                            <RadioGroup
                                value={selectedCourse}
                                onChange={e => setSelectedCourse(e.target.value)}
                            >
                                {courses.map(course => (
                                    <FormControlLabel
                                        key={course.id}
                                        value={course.id}
                                        control={<Radio size="small" />}
                                        label={course.title || course.id}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    </FormControl>

                    {/* Step 2: Select TSV file */}
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                            2. Select TSV file
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!selectedCourse}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ textTransform: 'none' }}
                            >
                                Choose file
                            </Button>
                            <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
                                {file ? file.name : 'No file selected'}
                            </Typography>
                        </Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".tsv,text/tab-separated-values"
                            style={{ display: 'none' }}
                            onChange={e => setFile(e.target.files?.[0] || null)}
                        />
                        {selectedCourse && file && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                                ⚠ Existing questions for this course will be replaced.
                            </Typography>
                        )}
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!selectedCourse || !file || uploading}
                        onClick={handleUpload}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        {uploading ? 'Importing…' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Bulk delete the currently checkbox-selected questions
const BulkDeleteButton = ({ selectedRows, onDeleted }) => {
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const count = selectedRows.length;

    const handleClick = () => {
        if (count === 0) {
            notify('Select one or more questions first', { type: 'info' });
            return;
        }
        setOpen(true);
    };

    const handleConfirm = async () => {
        setLoading(true);
        let ok = 0;
        let fail = 0;
        await Promise.all(
            selectedRows.map(async (row) => {
                try {
                    await httpClient(`${publicBase}/course_questions/${row.id}`, { method: 'DELETE' });
                    ok += 1;
                } catch (e) {
                    fail += 1;
                }
            })
        );
        setLoading(false);
        setOpen(false);
        if (fail === 0) notify(`Deleted ${ok} question(s)`, { type: 'success' });
        else notify(`Deleted ${ok}, failed ${fail}`, { type: 'warning' });
        onDeleted();
    };

    return (
        <>
            <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleClick}
                sx={{ textTransform: 'none' }}
            >
                {count > 0 ? `Bulk Delete (${count})` : 'Bulk Delete'}
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete selected questions"
                content={`Sure? Delete all the selected? (${count} question${count === 1 ? '' : 's'}). This cannot be undone.`}
                onConfirm={handleConfirm}
                onClose={() => setOpen(false)}
            />
        </>
    );
};

// Escape a value for a TSV cell (tabs/newlines would break the format)
const tsvCell = (v) => String(v ?? '').replace(/[\t\r\n]+/g, ' ').trim();

// Export the currently visible (filtered) rows to a TSV file
const ExportTsvButton = ({ rows }) => {
    const notify = useNotify();

    const handleExport = () => {
        if (!rows || rows.length === 0) {
            notify('Nothing to export', { type: 'info' });
            return;
        }
        const header = ['course_title', 'seq', 'question', 'answers', 'correct_answer'];
        const lines = [header.join('\t')];
        rows.forEach((r) => {
            const answers = Array.isArray(r.multiple_choices)
                ? r.multiple_choices.join(',')
                : (r.multiple_choices || '');
            lines.push([
                tsvCell(r.course_title),
                tsvCell(r.question_sequence),
                tsvCell(r.question),
                tsvCell(answers),
                tsvCell(r.correct_answer),
            ].join('\t'));
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/tab-separated-values' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'course_questions.tsv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        notify(`Exported ${rows.length} question(s)`, { type: 'success' });
    };

    return (
        <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: 'none' }}
        >
            Export TSV
        </Button>
    );
};

export const CourseQuestionList = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [selectionResetToken, setSelectionResetToken] = useState(0);
    const notify = useNotify();

    const handleBulkDeleted = useCallback(() => {
        setSelectionResetToken(t => t + 1);
        setSelectedRows([]);
        setRefreshTrigger(t => t + 1);
    }, []);

    const fetchQuestions = useCallback(() => {
        setLoading(true);
        const url = `${publicBase}/course_questions/?_start=0&_end=1000&_sort=course_title&_order=ASC`;
        httpClient(url)
            .then(({ json }) => {
                setQuestions(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching course questions:', error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions, refreshTrigger]);

    const columns = [
        {
            key: 'course_title',
            header: 'Course Title',
            render: (record) => (
                <span title={record.course_description || ''}>{record.course_title}</span>
            )
        },
        { key: 'question_sequence', header: 'Sequence' },
        { key: 'question', header: 'Question' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="course_questions" />
                    <DeleteCourseQuestionButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Course Questions</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <UploadTsvButton onSuccess={() => setRefreshTrigger(t => t + 1)} />
                    <BulkDeleteButton selectedRows={selectedRows} onDeleted={handleBulkDeleted} />
                    <ExportTsvButton rows={filteredRows} />
                    <Link
                        to="/course_questions/create"
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Create
                    </Link>
                </div>
            </div>
            <SortableTable
                data={questions}
                columns={columns}
                selectable
                onSelectionChange={setSelectedRows}
                onFilteredChange={setFilteredRows}
                selectionResetToken={selectionResetToken}
            />
        </div>
    );
};

const splitChoices = (data) => ({
    ...data,
    multiple_choices: typeof data.multiple_choices === 'string'
        ? data.multiple_choices.split(',').map(s => s.trim()).filter(Boolean)
        : data.multiple_choices,
});

const formatChoices = (val) => Array.isArray(val) ? val.join(',') : (val || '');

export const CourseQuestionEdit = () => (
    <Edit title="Edit Course Question" transform={splitChoices}>
        <SimpleForm>
            <h2>Course Questions</h2>
            <ReferenceInput source="course_material_id" reference="course_material_content">
                <SelectInput optionText="title" />
            </ReferenceInput>
            <NumberInput source="question_sequence" />
            <TextInput source="question" multiline fullWidth />
            <TextInput source="multiple_choices" helperText="Enter choices, separated by commas" format={formatChoices} />
            <NumberInput source="correct_answer" />
        </SimpleForm>
    </Edit>
);

export const CourseQuestionCreate = () => (
    <Create title="Create Course Question" transform={splitChoices}>
        <SimpleForm>
            <h2>Course Questions</h2>
            <ReferenceInput source="course_material_id" reference="course_material_content">
                <SelectInput optionText="title" />
            </ReferenceInput>
            <NumberInput source="question_sequence" />
            <TextInput source="question" multiline fullWidth />
            <TextInput source="multiple_choices" helperText="Enter choices, separated by commas" format={formatChoices} />
            <NumberInput source="correct_answer" />
        </SimpleForm>
    </Create>
);
